import { Container, Particle as PixiParticle, ParticleContainer, Rectangle, Sprite, Texture } from 'pixi.js';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '../../../constants';
import { WORLD_TO_R2 } from '../scale';

/**
 * Minimal pixel debris: 1-2px tinted quads on integer positions, fading in
 * three discrete alpha steps rather than a smooth ramp — a smooth fade reads as
 * a blur against native pixel art. No RNG: angles come from an even fan rotated
 * by the golden angle per burst, so successive bursts never line up and a run
 * repeats exactly.
 */
export const BURST_SMALL_COUNT = 10;
export const BURST_KILL_COUNT = 16;
const PARTICLE_CAP = 128;
const SPEED_MIN_PX_PER_SECOND = 30;
const SPEED_MAX_PX_PER_SECOND = 90;
const LIFE_MIN_MS = 250;
const LIFE_MAX_MS = 400;
const SIZE_SMALL_PX = 1;
const SIZE_LARGE_PX = 2;
const ALPHA_STEPS = Object.freeze([1, 0.66, 0.33]);
const GOLDEN_ANGLE_RAD = Math.PI * (3 - Math.sqrt(5));
const GOLDEN_RATIO_FRACTION = 0.618033988749895;
const TAU = Math.PI * 2;
const MS_PER_SECOND = 1000;

/** RGB read off the shipped `small-hit-spark` manifest, ramps.optical (upper end). */
const SPARK_COLORS = Object.freeze([0x3a9afa, 0x5cd0f1, 0x83f5ff]);
/** RGB read off the shipped `core-hit` manifest, ramps.warm plus the ember top. */
const KILL_COLORS = Object.freeze([0xe83045, 0xb6203a, 0xf53e77]);

export type BurstKind = 'small' | 'kill';

interface DesktopParticle {
  readonly sprite: Sprite;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  readonly lifeMs: number;
  ageMs: number;
}

export class PixelBurst {
  private readonly live: DesktopParticle[] = [];
  private readonly idle: Sprite[] = [];
  private readonly mobileParticles: PixiParticle[] = [];
  private readonly mobileLive = new Uint8Array(PARTICLE_CAP);
  private readonly mobileX = new Float32Array(PARTICLE_CAP);
  private readonly mobileY = new Float32Array(PARTICLE_CAP);
  private readonly mobileVx = new Float32Array(PARTICLE_CAP);
  private readonly mobileVy = new Float32Array(PARTICLE_CAP);
  private readonly mobileLifeMs = new Float32Array(PARTICLE_CAP);
  private readonly mobileAgeMs = new Float32Array(PARTICLE_CAP);
  private mobileLiveCount = 0;
  private mobileCursor = 0;
  private burstIndex = 0;
  private capReported = false;

  constructor(private readonly layer: Container, private readonly mobilePerformanceMode = false) {
    if (!mobilePerformanceMode) return;
    for (let i = 0; i < PARTICLE_CAP; i += 1) {
      this.mobileParticles.push(
        new PixiParticle({
          texture: Texture.WHITE,
          anchorX: 0.5,
          anchorY: 0.5,
          alpha: 0,
        }),
      );
    }
    layer.addChild(
      new ParticleContainer({
        texture: Texture.WHITE,
        particles: this.mobileParticles,
        boundsArea: new Rectangle(
          0,
          0,
          INTERNAL_WIDTH * WORLD_TO_R2,
          INTERNAL_HEIGHT * WORLD_TO_R2,
        ),
        roundPixels: true,
        dynamicProperties: {
          position: true,
          vertex: true,
          rotation: false,
          uvs: false,
          color: true,
        },
      }),
    );
  }

  get liveCount(): number {
    return this.mobilePerformanceMode ? this.mobileLiveCount : this.live.length;
  }

  burst(kind: BurstKind, centreX: number, centreY: number): void {
    const count = kind === 'kill' ? BURST_KILL_COUNT : BURST_SMALL_COUNT;
    const colors = kind === 'kill' ? KILL_COLORS : SPARK_COLORS;
    if (this.liveCount + count > PARTICLE_CAP) {
      this.reportCap();
      return;
    }
    const rotation = this.burstIndex * GOLDEN_ANGLE_RAD;
    this.burstIndex += 1;
    for (let i = 0; i < count; i += 1) {
      const spread = (i * GOLDEN_RATIO_FRACTION) % 1;
      const angle = rotation + (i / count) * TAU;
      const speed = SPEED_MIN_PX_PER_SECOND + spread * (SPEED_MAX_PX_PER_SECOND - SPEED_MIN_PX_PER_SECOND);
      const lifeMs = LIFE_MIN_MS + (1 - spread) * (LIFE_MAX_MS - LIFE_MIN_MS);
      const size = i % 2 === 0 ? SIZE_LARGE_PX : SIZE_SMALL_PX;
      if (this.mobilePerformanceMode) {
        const slot = this.takeMobileSlot();
        if (slot < 0) {
          this.reportCap();
          return;
        }
        const particle = this.mobileParticles[slot]!;
        this.mobileLive[slot] = 1;
        this.mobileX[slot] = centreX;
        this.mobileY[slot] = centreY;
        this.mobileVx[slot] = Math.cos(angle) * speed;
        this.mobileVy[slot] = Math.sin(angle) * speed;
        this.mobileLifeMs[slot] = lifeMs;
        this.mobileAgeMs[slot] = 0;
        this.mobileLiveCount += 1;
        particle.x = Math.round(centreX);
        particle.y = Math.round(centreY);
        particle.scaleX = size / Math.max(1, Texture.WHITE.width);
        particle.scaleY = size / Math.max(1, Texture.WHITE.height);
        particle.tint = colors[i % colors.length]!;
        particle.alpha = ALPHA_STEPS[0]!;
        continue;
      }
      const sprite = this.take();
      sprite.visible = true;
      sprite.alpha = ALPHA_STEPS[0]!;
      sprite.tint = colors[i % colors.length]!;
      sprite.setSize(size, size);
      sprite.position.set(Math.round(centreX), Math.round(centreY));
      this.live.push({
        sprite,
        x: centreX,
        y: centreY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        lifeMs,
        ageMs: 0,
      });
    }
  }

  update(deltaMs: number): void {
    if (this.mobilePerformanceMode) {
      this.updateMobile(deltaMs);
      return;
    }
    const seconds = deltaMs / MS_PER_SECOND;
    for (let i = this.live.length - 1; i >= 0; i -= 1) {
      const particle = this.live[i]!;
      particle.ageMs += deltaMs;
      if (particle.ageMs >= particle.lifeMs) {
        particle.sprite.visible = false;
        this.live.splice(i, 1);
        this.idle.push(particle.sprite);
        continue;
      }
      particle.x += particle.vx * seconds;
      particle.y += particle.vy * seconds;
      const step = Math.min(ALPHA_STEPS.length - 1, Math.floor((particle.ageMs / particle.lifeMs) * ALPHA_STEPS.length));
      particle.sprite.alpha = ALPHA_STEPS[step]!;
      particle.sprite.position.set(Math.round(particle.x), Math.round(particle.y));
    }
  }

  reset(): void {
    if (this.mobilePerformanceMode) {
      this.mobileLive.fill(0);
      this.mobileLiveCount = 0;
      for (const particle of this.mobileParticles) particle.alpha = 0;
      return;
    }
    for (const particle of this.live) {
      particle.sprite.visible = false;
      this.idle.push(particle.sprite);
    }
    this.live.length = 0;
  }

  private take(): Sprite {
    const parked = this.idle.pop();
    if (parked) return parked;
    const sprite = new Sprite(Texture.WHITE);
    sprite.roundPixels = true;
    this.layer.addChild(sprite);
    return sprite;
  }

  private takeMobileSlot(): number {
    for (let attempt = 0; attempt < PARTICLE_CAP; attempt += 1) {
      const slot = this.mobileCursor;
      this.mobileCursor = (slot + 1) % PARTICLE_CAP;
      if (this.mobileLive[slot] === 0) return slot;
    }
    return -1;
  }

  private updateMobile(deltaMs: number): void {
    const seconds = deltaMs / MS_PER_SECOND;
    for (let i = 0; i < PARTICLE_CAP; i += 1) {
      if (this.mobileLive[i] === 0) continue;
      const age = this.mobileAgeMs[i]! + deltaMs;
      const life = this.mobileLifeMs[i]!;
      if (age >= life) {
        this.mobileLive[i] = 0;
        this.mobileLiveCount -= 1;
        this.mobileParticles[i]!.alpha = 0;
        continue;
      }
      this.mobileAgeMs[i] = age;
      const x = this.mobileX[i]! + this.mobileVx[i]! * seconds;
      const y = this.mobileY[i]! + this.mobileVy[i]! * seconds;
      this.mobileX[i] = x;
      this.mobileY[i] = y;
      const step = Math.min(ALPHA_STEPS.length - 1, Math.floor((age / life) * ALPHA_STEPS.length));
      const particle = this.mobileParticles[i]!;
      particle.alpha = ALPHA_STEPS[step]!;
      particle.x = Math.round(x);
      particle.y = Math.round(y);
    }
  }

  /** Over the cap the request is dropped; one line, not one per frame. */
  private reportCap(): void {
    if (this.capReported) return;
    this.capReported = true;
    console.warn(`PixelBurst: ${PARTICLE_CAP} particle cap reached, further bursts dropped until it drains.`);
  }
}
